import type { ColumnType } from '@/types/domains/index';

/** 取得の状態。**「読み込み中」と「取れたけど0件」を区別する**ために持つ */
export type ColumnFeedState = {
  feed: Ref<ColumnType.Feed>;
  /** `useAsyncData` の `status`。`pending` のあいだは「0件」と区別できる */
  status: Ref<'idle' | 'pending' | 'success' | 'error'>;
};

const EMPTY_FEED: ColumnType.Feed = { indexUrl: '', articles: [] };

/**
 * コラム系セクション。note の RSS をサーバールート（`/api/note-column`）経由で取る。
 * 外部フィードなので、取れなくても例外にせず空を返して呼び出し側にカードを出させない。
 *
 * **クライアントでだけ取る**（`server: false`）。note は外部サービスで、落ちていると
 * トップの SSR がそのぶん待たされる。コラムはトップ最下部なので、後から生えても
 * 上のコンテンツは動かない。
 *
 * **`server: false` と「await しない」の両方が要る。** `server !== false` のとき
 * `useAsyncData` は `onServerPrefetch` に promise を積むため、top-level await を
 * 外すだけでは SSR のブロックは解消しない。
 */
export const useColumn = () => {
  /** @param feedId `COLUMN_FEEDS` の `id` */
  const fetchColumnFeed = (feedId: string): ColumnFeedState => {
    const { data, error, status } = useAsyncData<ColumnType.Feed>(
      `column-feed-${feedId}`,
      () => $fetch<ColumnType.Feed>('/api/note-column', { query: { feed: feedId } }),
      { server: false, lazy: true }
    );

    return {
      feed: computed<ColumnType.Feed>(() => (error.value || !data.value ? EMPTY_FEED : data.value)),
      status,
    };
  };

  return {
    fetchColumnFeed,
  };
};
